import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  getAppLink,
  getCurrentFacebookMe,
  getCurrentFacebookPages,
  getCurrentInstagramMe,
  getCurrentTwitterMe,
  getCurrentLinkedInMe,
  getCurrentThreadsMe,
} from "../../api/AppLinkApi";
import { getCurrentCanvaMe } from "../../api/CanvaApi";
import RuleAction from "../../actions/RuleAction";
import ProfileAvatar from "./ProfileAvatar";
import { fromJS } from "immutable";
import { getBaseUrl } from "../../api/api";
import { PackagePlus } from "lucide-react";

interface AppSocialProps {
  appId: string;
  connected: boolean;
  onConnect: () => void;
}

const AppSocial: React.FC<AppSocialProps> = ({ appId, connected, onConnect }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));
  const platformProfiles =
    useSelector((state: any) => state.ruleReducer.get("platformProfiles")) || fromJS({});
  const app = appLinks.toJS().find((item) => item.app == appId);
  const appImageUrl = app ? `${getBaseUrl()}${app.imageUrl}` : null;
  const appName = app ? app.name : "";

  const [loading, setLoading] = useState(false);

  const platformApiMap: Record<string, () => Promise<any>> = {
    facebook: getCurrentFacebookMe,
    instagram: getCurrentInstagramMe,
    twitter: getCurrentTwitterMe,
    linkedin: getCurrentLinkedInMe,
    threads: getCurrentThreadsMe,
    canva: getCurrentCanvaMe,
  };

  const transformProfileData = (platformId: string, data: any) => {
    if (!data) return null;

    switch (platformId) {
      case "facebook":
        return {
          picture: data?.pictureUrl,
          name: data?.name,
          username: data?.email,
        };
      case "instagram":
        return {
          picture: data?.profilePictureUrl,
          name: data?.name,
          username: data?.username,
        };
      case "twitter":
        return {
          picture: data?.profile_image_url,
          name: data?.name,
          username: data?.username,
        };
      case "linkedin":
        return {
          picture: data?.picture,
          name: `${data?.given_name || ""} ${data?.family_name || ""}`.trim(),
          username: data?.email,
        };
      case "threads":
        return {
          picture: data?.threads_profile_picture_url,
          name: data?.name,
          username: data?.username,
        };
      case "canva":
        return {
          picture: "",
          name: data?.profile?.display_name,
          username: "",
        };
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!connected) {
      return;
    }

    const existingProfile = platformProfiles.get(appId);
    if (existingProfile || loading) {
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        // For Facebook, mirror FacebookPost behavior: load both user and default Page
        if (appId === "facebook") {
          const [me, appLink] = await Promise.all([getCurrentFacebookMe(), getAppLink("facebook")]);

          const userProfile = transformProfileData("facebook", me);
          if (userProfile) {
            dispatch(
              RuleAction.setPlatformProfile({
                platformId: "facebook",
                profile: userProfile,
              })
            );
          }

          const pageId = appLink?.data?.page_id || "";
          if (pageId) {
            const pages = await getCurrentFacebookPages();
            const page = (pages || []).find((p: any) => String(p?.id) === String(pageId)) || null;

            if (page) {
              dispatch(
                RuleAction.setPlatformProfile({
                  platformId: "facebook-page",
                  profile: {
                    id: page.id,
                    name: page.name,
                    picture: page.pictureUrl || "",
                    username: page.id,
                  },
                })
              );
            }
          }
          return;
        }

        const apiCall = platformApiMap[appId];
        if (!apiCall) return;

        const data = await apiCall();
        const profile = transformProfileData(appId, data);
        if (profile) {
          dispatch(
            RuleAction.setPlatformProfile({
              platformId: appId,
              profile,
            })
          );
        }
      } catch (err) {
        // Silently fail - don't show error on preview
        console.error(`Failed to load profile for ${appId}:`, err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [appId, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!connected) {
    return (
      <div className="flex items-center gap-2">
        <ProfileAvatar picture={appImageUrl} name={appName} size="xs" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{appName}</div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onConnect();
            }}
            className="mt-1 flex items-center gap-1 px-2 text-xs text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <PackagePlus className="w-3 h-3" />
            {t("Connect")}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="relative h-[38px] w-[38px] flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="relative h-4 w-24 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          </div>
          <div className="relative h-3 w-32 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent [animation-delay:0.2s]" />
          </div>
        </div>
      </div>
    );
  }

  const platformBadge = appImageUrl ? (
    <div className="w-4 h-4 rounded-full flex items-center justify-center">
      <img alt={appName} src={appImageUrl} className="w-4 h-4" />
    </div>
  ) : null;

  const profile = platformProfiles.get(appId)?.toJS?.() || null;
  if (!profile) {
    return (
      <div className="flex items-center gap-2">
        <ProfileAvatar size="xs" platformBadge={platformBadge} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{t("Anonymous")}</div>
        </div>
      </div>
    );
  }

  const facebookPageProfile =
    appId === "facebook" ? platformProfiles.get("facebook-page")?.toJS?.() || null : null;

  return (
    <div className="flex items-center gap-2">
      <ProfileAvatar
        picture={profile.picture}
        name={profile.name}
        size="xs"
        platformBadge={platformBadge}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{profile.name}</div>
        {profile.username && (
          <div className="text-xs text-gray-500 truncate">
            {profile.username.startsWith("@") ? profile.username : `@${profile.username}`}
          </div>
        )}

        {facebookPageProfile && (
          <div className="text-xs font-semibold text-gray-500 truncate">
            Page: {facebookPageProfile.name}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSocial;
